import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { MSRemoteLoggerCategory, MSRemoteLoggerSeverity } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';
import { UserStorageAdapter } from 'src/app/adapters/user-storage/user-storage.adapter';
import { RemoteLoggerUseCase } from '../remote-logger/remote-logger.use-case';

const studyServerUrlKey = 'studyServerUrl';
const localeKey = 'locale';

@Injectable({
  providedIn: 'root'
})
export class UserStorageUseCase {
  constructor(
    private remoteLoggerUseCase: RemoteLoggerUseCase,
    private userStorageAdapter: UserStorageAdapter,
  ) {}

  migrateIfNeeded(): Observable<void> {
    return this.userStorageAdapter
      .migrateToPrefsIfNeeded(studyServerUrlKey)
      .pipe(
        switchMap(() => {
          return this.userStorageAdapter.migrateToPrefsIfNeeded(localeKey)
        }),
        catchError((error: Error) => {
            this.remoteLoggerUseCase.addBreadcrumbs(
              'Migration failed',
              MSRemoteLoggerCategory.USER_STORAGE_SERVICE,
              MSRemoteLoggerSeverity.ERROR,
              { error: error.message }
            )

            return of(undefined)
        }),
      );
  }

  getServerAddress(): Observable<string> {
    const serverUrlObs = this.userStorageAdapter.get<string>(studyServerUrlKey)
      .pipe(
        switchMap((url) => of(url)),
        tap(
          (url) => this.remoteLoggerUseCase.addBreadcrumbs(
            'Get server address',
            MSRemoteLoggerCategory.USER_STORAGE_SERVICE,
            MSRemoteLoggerSeverity.INFO,
            { url }
          ),
          (error: Error) => this.remoteLoggerUseCase.addBreadcrumbs(
            'Get server address failed',
            MSRemoteLoggerCategory.USER_STORAGE_SERVICE,
            MSRemoteLoggerSeverity.ERROR,
            { error: error.message }
          )
        )
      );
    return serverUrlObs;
  }

  setServerAddress(url: string): Observable<void> {
    const setStudyServerUrlObs = this.userStorageAdapter.set(studyServerUrlKey, url)
      .pipe(
        tap(
          (_) => this.remoteLoggerUseCase.addBreadcrumbs(
            'Set server address',
            MSRemoteLoggerCategory.USER_STORAGE_SERVICE,
            MSRemoteLoggerSeverity.INFO,
            { url }
          ),
          (error: Error) => this.remoteLoggerUseCase.addBreadcrumbs(
            'Set server address failed',
            MSRemoteLoggerCategory.USER_STORAGE_SERVICE,
            MSRemoteLoggerSeverity.ERROR,
            { error: error.message }
          )
        )
      );
    return setStudyServerUrlObs;
  }

  getLocale(): Observable<string | undefined> {
    const localeObs = this.userStorageAdapter.get<string>(localeKey)
      .pipe(
        tap(
          (locale) => this.remoteLoggerUseCase.addBreadcrumbs(
            'Get Locale',
            MSRemoteLoggerCategory.USER_STORAGE_SERVICE,
            MSRemoteLoggerSeverity.INFO,
            { locale }
          ),
          (error: Error) => this.remoteLoggerUseCase.addBreadcrumbs(
            'Get Locale failed',
            MSRemoteLoggerCategory.USER_STORAGE_SERVICE,
            MSRemoteLoggerSeverity.ERROR,
            { error: error.message }
          )
        )
      );
    return localeObs;
  }

  setLocale(locale: string): Observable<void> {
    const setLocaleObs = this.userStorageAdapter.set(localeKey, locale)
      .pipe(
        tap(
          (_) => this.remoteLoggerUseCase.addBreadcrumbs(
            'Set Locale',
            MSRemoteLoggerCategory.USER_STORAGE_SERVICE,
            MSRemoteLoggerSeverity.INFO,
            { locale }
          ),
          (error: Error) => this.remoteLoggerUseCase.addBreadcrumbs(
            'Set Locale failed',
            MSRemoteLoggerCategory.USER_STORAGE_SERVICE,
            MSRemoteLoggerSeverity.ERROR,
            { error: error.message }
          )
        )
      );
    return setLocaleObs;
  }
}
